import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ColorResult } from 'react-color';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { SelectGroupOption } from '../../general-components/select-group';
import SelectSmall from '../../general-components/select-small';
import ColorPicker from './color-picker';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import TitleCard from '../../general-components/title-card';
import { GreenroomState } from '../../../stores/types/types';
import { GreenroomUser } from '../../../stores/reducers/session';
import { formatHex } from '../../../utils/utils';
import { AppSettings } from '../../../stores/reducers/settings';

interface Props {
	settingsForShotEdited: (newQueue: any) => void;
	appliedMatchesQueue: boolean;
	queue: AppSettings;
}

const animationOptions: SelectGroupOption[] = [
	{ label: 'Dissolve', value: 'dissolve' },
	{ label: 'Move In', value: 'move-in' },
	{ label: 'None', value: 'none' },
];

const durationOptions: SelectGroupOption[] = [
	{ label: 'All Time', value: null },
	{ label: '5 sec.', value: 5 },
	{ label: '10 sec.', value: 10 },
];

export const titleDefault: GreenroomUser = {
	first_name: 'Name',
	last_name: '',
	location: 'Location',
	title: 'Title',
	avatar: '',
	gr_user: 0,
	email: '',
	bg_image: '',
	tutorial_video_count: 0,
};

export default function Titles(props: Props) {
	const { queue } = props;
	const { appliedMatchesQueue, settingsForShotEdited } = props;
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const colorDebounce = useRef<NodeJS.Timeout | null>(null);
	const [font1, setFont1] = useState<any>({ hex: queue.titlesFontColor1 });
	const [font2, setFont2] = useState<any>({ hex: queue.titlesFontColor2 });
	const [bg1, setBg1] = useState<any>({ hex: queue.titlesBackgroundColor1 });
	const [bg2, setBg2] = useState<any>({ hex: queue.titlesBackgroundColor2 });
	const [bg3, setBg3] = useState<any>({ hex: queue.titlesBackgroundColor3 });

	const [svgDropdownVisible, setSvgDropdownVisible] = useState(false);
	const [showColorPickers, setShowColorPickers] = useState({
		font1: false,
		font2: false,
		background1: false,
		background2: false,
		background3: false,
	});
	const selectedAnimation = animationOptions.find(
		(option) => option.value === queue.titlesAnimation,
	);
	const selectedDuration = durationOptions.find((option) => option.value === queue.titlesDuration);

	const exampleUser = user ? { ...user } : titleDefault;

	function toggleSvgDropdownVisible() {
		setSvgDropdownVisible(!svgDropdownVisible);
	}

	useEffect(() => {
		if (appliedMatchesQueue) {
			setFont1({ hex: queue.titlesFontColor1 });
			setFont2({ hex: queue.titlesFontColor2 });
			setBg1({ hex: queue.titlesBackgroundColor1 });
			setBg2({ hex: queue.titlesBackgroundColor2 });
			setBg3({ hex: queue.titlesBackgroundColor3 });
		}
	}, [appliedMatchesQueue]);

	function toggleColorPicker(colorPicker: string) {
		switch (colorPicker) {
			case 'font1':
				setShowColorPickers({ ...showColorPickers, font1: !showColorPickers.font1 });
				break;
			case 'font2':
				setShowColorPickers({ ...showColorPickers, font2: !showColorPickers.font2 });
				break;
			case 'bg1':
				setShowColorPickers({ ...showColorPickers, background1: !showColorPickers.background1 });
				break;
			case 'bg2':
				setShowColorPickers({ ...showColorPickers, background2: !showColorPickers.background2 });
				break;
			case 'bg3':
				setShowColorPickers({ ...showColorPickers, background3: !showColorPickers.background3 });
				break;
			default: break;
		}
	}

	function closeColorPicker() {
		setShowColorPickers({
			font1: false,
			font2: false,
			background1: false,
			background2: false,
			background3: false,
		});
	}

	function handleCheckbox(type: string) {
		const newQueue = { ...queue };
		switch (type) {
			case 'location':
				newQueue.titlesUseLocation = !queue.titlesUseLocation;
				settingsForShotEdited(newQueue);
				break;
			case 'name':
				newQueue.titlesUseName = !queue.titlesUseName;
				settingsForShotEdited(newQueue);
				break;
			case 'title':
				newQueue.titlesUseTitle = !queue.titlesUseTitle;
				settingsForShotEdited(newQueue);
				break;
			default: break;
		}
	}

	function changeSelectedTitleCard(cardType: number) {
		setSvgDropdownVisible(!svgDropdownVisible);
		const newQueue = { ...queue, titlesSelectedCard: cardType };
		settingsForShotEdited(newQueue);
	}

	function changeFont1(color: ColorResult) {
		setFont1(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesFontColor1: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeFont2(color: ColorResult) {
		setFont2(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesFontColor2: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeBG1(color: ColorResult) {
		setBg1(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesBackgroundColor1: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeBG2(color: ColorResult) {
		setBg2(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesBackgroundColor2: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeBG3(color: ColorResult) {
		setBg3(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesBackgroundColor3: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeTitlesAnimation(animation: String) {
		const newQueue = { ...queue, titlesAnimation: animation };
		settingsForShotEdited(newQueue);
	}

	function changeTitlesDuration(duration: Number) {
		const newQueue = { ...queue, titlesDuration: duration };
		settingsForShotEdited(newQueue);
	}

	function changeTitlesPosition(position: string) {
		const newQueue = { ...queue, titlesPosition: position };
		settingsForShotEdited(newQueue);
	}

	return (
		<>
			<div className="settings-container titles-container">
				<div className={classNames('titles-options')}>

					<label className="section-title">STYLE</label>

					<div className="title-checkboxes">
						<div className="checkbox-settings-wrapper">
							<div className={classNames('checkbox-settings', { selected: queue.titlesUseName })} onClick={() => { handleCheckbox('name'); }}>
								<div className="checkbox-settings-outer">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<label>Name</label>
							</div>
						</div>
						<div className="checkbox-settings-wrapper">
							<div className={classNames('checkbox-settings', { selected: queue.titlesUseTitle })} onClick={() => { handleCheckbox('title'); }}>
								<div className="checkbox-settings-outer">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<label>Title</label>
							</div>
						</div>
						<div className="checkbox-settings-wrapper">
							<div className={classNames('checkbox-settings', { selected: queue.titlesUseLocation })} onClick={() => { handleCheckbox('location'); }}>
								<div className="checkbox-settings-outer">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<label>Location</label>
							</div>
						</div>
					</div>

					<div className="titles-dropdown-wrapper">
						<div className="titles-dropdown" onClick={toggleSvgDropdownVisible}>
							<div className="titles-dropdown-option preview">
								<TitleCard cardType={queue.titlesSelectedCard} user={exampleUser} preview={true} />
								<span className="arrow"><Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.DEFAULT_GRAY} size={10} /></span>
							</div>
						</div>

						{svgDropdownVisible && (
							<>
								<div className="titles-dropdown-overlay" onClick={toggleSvgDropdownVisible}></div>
								<div className="titles-dropdown-active">
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(1)}>
										<TitleCard cardType={1} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(2)}>
										<TitleCard cardType={2} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(3)}>
										<TitleCard cardType={3} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(4)}>
										<TitleCard cardType={4} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(5)}>
										<TitleCard cardType={5} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(6)}>
										<TitleCard cardType={6} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(7)}>
										<TitleCard cardType={7} user={exampleUser} preview={true} />
									</div>
								</div>
							</>
						)}
					</div>

					<div className="titles-row">
						<label className='titles-row-group'>Font colors</label>

						<div className="color-toggle" onClick={() => toggleColorPicker('font1')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesFontColor1 }}></div>
							<span className='format-hex'>{queue.titlesFontColor1}</span>
						</div>
						<ColorPicker
							value={formatHex(font1.hex)}
							color={font1}
							label={'Primary font color'}
							open={showColorPickers.font1}
							close={closeColorPicker}
							onChange={changeFont1}
						/>
					</div>
					<div className="titles-row">
						<label></label>
						<div className="color-toggle" onClick={() => toggleColorPicker('font2')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesFontColor2 }}></div>
							<span className='format-hex'>{queue.titlesFontColor2}</span>
						</div>
						<ColorPicker
							value={formatHex(font2.hex)}
							color={font2}
							label={'Secondary font color'}
							open={showColorPickers.font2}
							close={closeColorPicker}
							onChange={changeFont2}
						/>
					</div>

					<hr />

					<div className="titles-row">
						<label className='titles-row-group'>Background colors</label>

						<div className="color-toggle" onClick={() => toggleColorPicker('bg1')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesBackgroundColor1 }}></div>
							<span className='format-hex'>{queue.titlesBackgroundColor1}</span>
						</div>
						<ColorPicker
							value={formatHex(bg1.hex)}
							color={bg1}
							label={'Primary background color'}
							open={showColorPickers.background1}
							close={closeColorPicker}
							onChange={changeBG1}
						/>
					</div>
					<div className="titles-row">
						<label></label>
						<div className="color-toggle" onClick={() => toggleColorPicker('bg2')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesBackgroundColor2 }}></div>
							<span className='format-hex'>{queue.titlesBackgroundColor2}</span>
						</div>
						<ColorPicker
							value={formatHex(bg2.hex)}
							color={bg2}
							label={'Secondary background color'}
							open={showColorPickers.background2}
							close={closeColorPicker}
							onChange={changeBG2}
						/>
					</div>
					<div className="titles-row">
						<label></label>
						<div className="color-toggle" onClick={() => toggleColorPicker('bg3')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesBackgroundColor3 }}></div>
							<span className='format-hex'>{queue.titlesBackgroundColor3}</span> </div>
						<ColorPicker
							value={formatHex(bg3.hex)} color={bg3}
							label={'Tertiary background color'}
							open={showColorPickers.background3}
							close={closeColorPicker}
							onChange={changeBG3}
						/>
					</div>

					<hr />

					<div className="titles-row">
						<label className='titles-row-group'>Animation</label>
						<SelectSmall
							options={animationOptions}
							label={selectedAnimation?.label || animationOptions[0].label}
							selected={selectedAnimation || animationOptions[0]}
							dropdown={true}
							onChange={changeTitlesAnimation}
							dropdownPosition={'drop-left'}
						/>
					</div>
					<div className="titles-row">
						<label className='titles-row-group'>Visible for</label>
						<SelectSmall
							options={durationOptions}
							label={selectedDuration?.label || durationOptions[0].label}
							selected={selectedDuration || durationOptions[0]}
							dropdown={true}
							onChange={changeTitlesDuration}
							dropdownPosition={'drop-left'}
						/>
					</div>

					{(queue.titlesSelectedCard === 3) && <>
						<hr />

						<div className="titles-row">
							<label>Position</label>

							<div className="corner-markers">
								<div className={classNames('corner-marker bottom-left', { active: queue.titlesPosition === 'bottom-left' })} onClick={() => changeTitlesPosition('bottom-left')}>
									<span>&#9698;</span>
								</div>
								<div className={classNames('corner-marker top-left', { active: queue.titlesPosition === 'top-left' })} onClick={() => changeTitlesPosition('top-left')}>
									<span>&#9698;</span>
								</div>
								<div className={classNames('corner-marker top-right', { active: queue.titlesPosition === 'top-right' })} onClick={() => changeTitlesPosition('top-right')}>
									<span>&#9698;</span>
								</div>
								<div className={classNames('corner-marker bottom-right', { active: queue.titlesPosition === 'bottom-right' })} onClick={() => changeTitlesPosition('bottom-right')}>
									<span>&#9698;</span>
								</div>
							</div>
						</div>
					</>}

				</div>
			</div>
		</>
	);
}
